import React, { useEffect, useRef, useState } from "react";
import { DrawerForm } from "../../../components/drawers/DrawerForm";
import ToggleSwitch from "../../../components/form/ToggleSwitch";
import Indicator from "../../../components/base/Indicator";
import Icon from "../../../components/base/Icon";
import { useHub } from "../../../core/Global";
import { toastError, toastSuccess } from "../../../components/base/Toast";
import { EnterpriseSettings, Quote } from "../models/Enterprise";
import { toCount, toMoney, toRate, toUnit } from "../../plancatalog/components/format";
import TagifyAccounts, { AccountSuggestion } from "../../../components/tagify/TagifyAccounts";
import PhoneInput from "../../../components/form/PhoneInput";
import PhoneItem from "../../../models/PhoneItem";
import TextBox from "../../../components/form/TextBox";
import TextArea from "../../../components/form/TextArea";
import DropDown from "../../../components/form/DropDown";
import DatePicker from "../../../components/form/DatePicker";
import moment from "moment";


// A line as it is being typed: numbers stay as text until they are sent to be priced.
interface FormLine {
    key: string
    label: string
    unit: string
    quantity: string
    unitPrice: string
    overridden: boolean
    unitCost: string
    oneTime: boolean
}

interface Form {
    id?: string
    companyName: string
    contactName: string
    contactEmail: string
    contactPhone: string
    contactPhoneItem?: PhoneItem
    existingUserId?: string
    lines: FormLine[]
    overageMessageRate: string
    overageMinuteRate: string
    extraNumberPrice: string
    whatsAppIncluded: boolean
    telegramIncluded: boolean
    extensionsSupportIncluded: boolean
    callRecordingIncluded: boolean
    recordingRetentionMonths: string
    supportTier: string
    termMonths: string
    billing: "monthly" | "annual"
    autoRenew: boolean
    renewalUpliftPercent: string
    validUntil: string
    customerNote: string
    internalNote: string
}

const num = (v: string) => {
    const n = parseFloat((v ?? "").replace(/,/g, ""))
    return isNaN(n) ? 0 : n
}

const dateInput = (d: Date) => d.toISOString().substring(0, 10)

const line = (key: string, quantity: number): FormLine => ({
    key, label: "", unit: "", quantity: String(quantity), unitPrice: "0", overridden: false, unitCost: "0", oneTime: false
})

const formOf = (q: Quote | undefined, validDays: number): Form => q ? {
    id: q.id,
    companyName: q.companyName ?? "",
    contactName: q.contactName ?? "",
    contactEmail: q.contactEmail ?? "",
    contactPhone: q.contactPhone ?? "",
    contactPhoneItem: q.contactPhoneItem,
    existingUserId: q.existingUserId,
    lines: q.lines.map(l => ({
        key: l.key ?? "custom", label: l.label ?? "", unit: l.unit ?? "", quantity: String(l.quantity),
        unitPrice: String(l.unitPrice), overridden: !!l.overridden, unitCost: String(l.unitCost ?? 0), oneTime: !!l.oneTime
    })),
    overageMessageRate: String(q.overageMessageRate),
    overageMinuteRate: String(q.overageMinuteRate),
    extraNumberPrice: String(q.extraNumberPrice),
    whatsAppIncluded: !!q.whatsAppIncluded,
    telegramIncluded: !!q.telegramIncluded,
    extensionsSupportIncluded: !!q.extensionsSupportIncluded,
    callRecordingIncluded: !!q.callRecordingIncluded,
    recordingRetentionMonths: String(q.recordingRetentionMonths),
    supportTier: q.supportTier ?? "",
    termMonths: String(q.termMonths),
    billing: q.billing,
    autoRenew: !!q.autoRenew,
    renewalUpliftPercent: String(q.renewalUpliftPercent),
    validUntil: dateInput(new Date(q.validUntil)),
    customerNote: q.customerNote ?? "",
    internalNote: q.internalNote ?? ""
} : {
    companyName: "", contactName: "", contactEmail: "", contactPhone: "",
    lines: [line("users", 25), line("messages", 50000), line("minutes", 15000), line("numbers", 5)],
    overageMessageRate: "0.015", overageMinuteRate: "0.03", extraNumberPrice: "3",
    whatsAppIncluded: true, telegramIncluded: true, extensionsSupportIncluded: true, callRecordingIncluded: true,
    recordingRetentionMonths: "12", supportTier: "Named manager",
    termMonths: "12", billing: "monthly", autoRenew: true, renewalUpliftPercent: "5",
    validUntil: dateInput(new Date(Date.now() + validDays * 86400000)),
    customerNote: "", internalNote: ""
}

const bodyOf = (f: Form) => ({
    id: f.id,
    companyName: f.companyName,
    contactName: f.contactName,
    contactEmail: f.contactEmail,
    contactPhone: f.contactPhoneItem?.phoneFriendly ?? f.contactPhone,
    contactPhoneItem: f.contactPhoneItem,
    existingUserId: f.existingUserId,
    lines: f.lines.map(l => ({
        key: l.key, label: l.label, unit: l.unit, quantity: num(l.quantity),
        unitPrice: num(l.unitPrice), overridden: l.overridden, unitCost: num(l.unitCost), oneTime: l.oneTime
    })),
    overageMessageRate: num(f.overageMessageRate),
    overageMinuteRate: num(f.overageMinuteRate),
    extraNumberPrice: num(f.extraNumberPrice),
    whatsAppIncluded: f.whatsAppIncluded,
    telegramIncluded: f.telegramIncluded,
    extensionsSupportIncluded: f.extensionsSupportIncluded,
    // Every call is recorded on every account.
    callRecordingIncluded: true,
    recordingRetentionMonths: num(f.recordingRetentionMonths),
    supportTier: f.supportTier,
    termMonths: num(f.termMonths),
    billing: f.billing,
    autoRenew: f.autoRenew,
    renewalUpliftPercent: num(f.renewalUpliftPercent),
    validUntil: f.validUntil,
    customerNote: f.customerNote,
    internalNote: f.internalNote
})


// Building, or editing, a draft offer (spec A7). Every figure on the right is the hub's own
// calculation of what is on the left, asked for as it is typed -- the same calculation that
// decides what the customer is charged.
export default function QuoteBuilderDrawer(props: {
    quote?: Quote,
    onClose: () => void,
    onSaved: (q: Quote) => void
}) {

    const hub = useHub()

    const [settings, settingsSet] = useState<EnterpriseSettings>()
    const [form, formSet] = useState<Form>()
    const [calc, calcSet] = useState<Quote>()
    const [pricing, pricingSet] = useState<boolean>(false)

    // New customer, or one who already has a Rinvox account and is being re-quoted.
    const [existingMode, existingModeSet] = useState<boolean>(!!props.quote?.existingUserId)
    // The phone input reads its value once; this remounts it when an account fills it in.
    const [phoneKey, phoneKeySet] = useState<number>(0)

    const timer = useRef<any>()

    useEffect(() => {
        hub.Get("/system/enterprise/settings", res => {
            if (!res.success) return toastError(res.message ?? "Couldn't load the settings.")
            settingsSet(res.data)
            formSet(formOf(props.quote, res.data?.defaultValidDays ?? 14))
        })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    // Priced again a moment after the typing stops.
    useEffect(() => {
        if (!form) return
        clearTimeout(timer.current)
        timer.current = setTimeout(() => {
            pricingSet(true)
            hub.Post("/system/enterprise/calculate", bodyOf(form), res => {
                pricingSet(false)
                if (res.success) calcSet(res.data)
            }, () => pricingSet(false))
        }, 350)
        return () => clearTimeout(timer.current)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [form])

    const set = <K extends keyof Form>(key: K, value: Form[K]) => formSet(f => f ? { ...f, [key]: value } : f)

    // The contact fields follow the chosen account: picking one writes its company, name, email
    // and phone over whatever was there, and clearing it empties them again, so the fields never
    // describe a different customer from the account the offer is for.
    const pickAccount = (a?: AccountSuggestion) => {
        formSet(f => {
            if (!f) return f
            if (a) return {
                ...f,
                existingUserId: a.id,
                companyName: a.company ?? "",
                contactName: a.name ?? "",
                contactEmail: a.email ?? "",
                contactPhone: a.phone ?? "",
                contactPhoneItem: a.phoneItem
            }
            // Nothing was chosen, so there is nothing of an account's to take away.
            if (!f.existingUserId) return f
            return {
                ...f,
                existingUserId: undefined,
                companyName: "",
                contactName: "",
                contactEmail: "",
                contactPhone: "",
                contactPhoneItem: undefined
            }
        })
        // The phone input only reads its value when it mounts.
        phoneKeySet(k => k + 1)
    }
    const setLine = (i: number, patch: Partial<FormLine>) =>
        formSet(f => f ? { ...f, lines: f.lines.map((l, j) => j === i ? { ...l, ...patch } : l) } : f)

    const save = (callback: any) => {
        if (!form) return callback({ success: false })
        hub.Post("/system/enterprise/save", bodyOf(form), res => {
            callback(res)
            if (res.success) {
                toastSuccess(`Q-${res.data.number} saved as a draft.`)
                props.onSaved(res.data)
            }
            else toastError(res.message ?? "Couldn't save the offer.")
        }, () => callback({ success: false }))
    }

    const itemOf = (key: string) => settings?.items.find(i => i.key === key)

    return (
        <DrawerForm id="drawer-enterprise-builder"
            title={props.quote ? `Edit Q-${props.quote.number}${props.quote.version > 1 ? " v" + props.quote.version : ""}` : "New Enterprise offer"}
            titleIcon="file-signature"
            subtitle="A draft until it is sent; frozen from then on"
            size="xlarge"
            buttonText="Save draft"
            confirmDisabled={!form || (existingMode && !form.existingUserId)}
            onClose={props.onClose}
            onSubmit={(body, callback) => save(callback)}>

            {
                !form || !settings ?
                    <div className="py-20"><Indicator /></div>
                    :
                    <div className="ent-builder">

                        <div className="ent-builder-main">

                            {/* ------------------------------------------------ customer */}
                            <div className="ent-section">
                                <div className="ent-section-title">Customer</div>

                                <div className="ent-kind">
                                    <button type="button" className={"ent-kind-option" + (!existingMode ? " is-active" : "")}
                                        onClick={e => { e.preventDefault(); existingModeSet(false); pickAccount(undefined) }}>
                                        <Icon icon="user-plus" size={14} />
                                        <div>
                                            <b>New customer</b>
                                            <span>Creates their account from the link and pays by card</span>
                                        </div>
                                    </button>
                                    <button type="button" className={"ent-kind-option" + (existingMode ? " is-active" : "")}
                                        onClick={e => { e.preventDefault(); existingModeSet(true) }}>
                                        <Icon icon="user-check" size={14} />
                                        <div>
                                            <b>Existing Rinvox customer</b>
                                            <span>Their account switches to this plan; a period already paid runs on</span>
                                        </div>
                                    </button>
                                </div>

                                {
                                    existingMode &&
                                    <div className="mb-5">
                                        <TagifyAccounts id="ent-account-picker" apiUrl="/system/enterprise/accounts"
                                            selectedId={form.existingUserId}
                                            placeholder="Find the account — name, username or email"
                                            onSelect={pickAccount} />
                                    </div>
                                }

                                <div className="ent-grid">
                                    <TextBox id="ent-company" name="companyName" label="Company" labelUp block noMarginBottom
                                        value={form.companyName} onTextChanges={v => set("companyName", v)} />
                                    <TextBox id="ent-contact-name" name="contactName" label="Contact name" labelUp block noMarginBottom
                                        value={form.contactName} onTextChanges={v => set("contactName", v)} />
                                    <TextBox id="ent-contact-email" name="contactEmail" label="Contact email" labelUp block noMarginBottom email
                                        hint="The offer and its link go here"
                                        value={form.contactEmail} onTextChanges={v => set("contactEmail", v)} />
                                    <PhoneInput key={"ent-phone-" + phoneKey} id={"ent-contact-phone-" + phoneKey} name="contactPhone"
                                        label="Contact phone" labelUp block noMarginBottom countryCodes="all"
                                        defaultPhone={form.contactPhoneItem}
                                        onChange={p => set("contactPhoneItem", p)} />
                                </div>
                            </div>

                            {/* ------------------------------------------------ lines */}
                            <div className="ent-section">
                                <div className="ent-section-title">What is sold</div>
                                <div className="ent-lines">
                                    <div className="ent-line is-head">
                                        <span>Item</span><span>Quantity</span><span>Unit price</span><span>Total</span><span />
                                    </div>
                                    {
                                        form.lines.map((l, i) => {
                                            const item = itemOf(l.key)
                                            const priced = calc?.lines?.[i]
                                            const custom = !item
                                            return (
                                                <div key={i} className={"ent-line" + (l.overridden ? " is-overridden" : "")}>
                                                    <div className="min-w-0">
                                                        {
                                                            custom ?
                                                                <>
                                                                    <TextBox id={"ent-line-label-" + i} name={"lineLabel" + i} block noMarginBottom marginBottom={1}
                                                                        placeholder="Custom line" value={l.label} onTextChanges={v => setLine(i, { label: v })} />
                                                                    <div className="d-flex gap-2 align-items-center mt-1">
                                                                        <TextBox id={"ent-line-unit-" + i} name={"lineUnit" + i} block noMarginBottom
                                                                            placeholder="unit" value={l.unit} onTextChanges={v => setLine(i, { unit: v })} />
                                                                        <TextBox id={"ent-line-cost-" + i} name={"lineCost" + i} block noMarginBottom decimal
                                                                            placeholder="cost" tooltip="What one unit costs Rinvox" value={l.unitCost} onTextChanges={v => setLine(i, { unitCost: v })} />
                                                                        <ToggleSwitch raw label="Once" checked={l.oneTime} onChange={v => setLine(i, { oneTime: v })} />
                                                                    </div>
                                                                </>
                                                                :
                                                                <>
                                                                    <div className="fw-semibold text-gray-900 text-truncate">{item.label}</div>
                                                                    <div className="text-gray-500 fs-8">{item.oneTime ? "one-time" : item.unit} · cost {toUnit(item.unitCost)}</div>
                                                                </>
                                                        }
                                                    </div>
                                                    <TextBox id={"ent-line-qty-" + i} name={"lineQty" + i} block noMarginBottom numeric
                                                        value={l.quantity} onTextChanges={v => setLine(i, { quantity: v })} />
                                                    <div>
                                                        <TextBox id={"ent-line-price-" + i} name={"linePrice" + i} block noMarginBottom decimal
                                                            value={custom || l.overridden ? l.unitPrice : String(priced?.listUnitPrice ?? "")}
                                                            onTextChanges={v => setLine(i, { unitPrice: v, overridden: !custom })} />
                                                        {
                                                            !custom && l.overridden && priced &&
                                                            <a href="#auto" className="fs-9" onClick={e => { e.preventDefault(); setLine(i, { overridden: false }) }}>
                                                                back to {toUnit(priced.listUnitPrice)}
                                                            </a>
                                                        }
                                                    </div>
                                                    <div className="fw-bold text-gray-900 text-end">
                                                        {toMoney(priced?.total)}
                                                        {priced && priced.listTotal !== priced.total && <div className="fs-9 text-gray-500 text-decoration-line-through">{toMoney(priced.listTotal)}</div>}
                                                    </div>
                                                    <button type="button" className="btn btn-icon btn-sm btn-light-danger"
                                                        onClick={e => { e.preventDefault(); set("lines", form.lines.filter((_, j) => j !== i)) }}>
                                                        <Icon icon="xmark" size={11} />
                                                    </button>
                                                </div>
                                            )
                                        })
                                    }
                                </div>
                                <div className="d-flex flex-wrap gap-2 mt-3">
                                    {
                                        settings.items.map(it =>
                                            <button key={it.key} type="button" className="btn btn-sm btn-light"
                                                onClick={e => { e.preventDefault(); set("lines", [...form.lines, line(it.key!, 1)]) }}>
                                                <Icon icon="plus" size={10} className="me-1" />{it.label}
                                            </button>
                                        )
                                    }
                                    <button type="button" className="btn btn-sm btn-light-primary"
                                        onClick={e => { e.preventDefault(); set("lines", [...form.lines, line("custom", 1)]) }}>
                                        <Icon icon="plus" size={10} className="me-1" />Custom line
                                    </button>
                                </div>
                                <div className="ent-note">
                                    Volume tiers set the unit price automatically. Typing a different price overrides it, and that difference is the discount the approval is based on.
                                </div>
                            </div>

                            {/* ------------------------------------------------ overage */}
                            <div className="ent-section">
                                <div className="ent-section-title">Beyond the allowance <span className="text-danger">*</span></div>
                                <div className="ent-grid is-3">
                                    <TextBox id="ent-over-msg" name="overageMessageRate" label="Each message" labelUp block noMarginBottom decimal
                                        iconText="$" iconStart hint={`cost ${toRate(settings.messageCost)}`}
                                        value={form.overageMessageRate} onTextChanges={v => set("overageMessageRate", v)} />
                                    <TextBox id="ent-over-min" name="overageMinuteRate" label="Each call minute" labelUp block noMarginBottom decimal
                                        iconText="$" iconStart hint={`cost ${toRate(settings.minuteCost)}`}
                                        value={form.overageMinuteRate} onTextChanges={v => set("overageMinuteRate", v)} />
                                    <TextBox id="ent-over-num" name="extraNumberPrice" label="Each extra number / month" labelUp block noMarginBottom money
                                        value={form.extraNumberPrice} onTextChanges={v => set("extraNumberPrice", v)} />
                                </div>
                                <div className="ent-note">
                                    Never blank: committed volume with no overage rate is an unlimited plan given away by accident.
                                    Extra users beyond the quote are {toMoney(calc?.extraUserPrice)} each, the standard price.
                                </div>
                            </div>

                            {/* ------------------------------------------------ included */}
                            <div className="ent-section">
                                <div className="ent-section-title">Included</div>
                                <div className="ent-grid">
                                    <ToggleSwitch raw label="WhatsApp" checked={form.whatsAppIncluded} onChange={v => set("whatsAppIncluded", v)} />
                                    <ToggleSwitch raw label="Telegram" checked={form.telegramIncluded} onChange={v => set("telegramIncluded", v)} />
                                    <ToggleSwitch raw label="Masked Job Calls" checked={form.extensionsSupportIncluded} onChange={v => set("extensionsSupportIncluded", v)} />
                                </div>
                                <div className="ent-grid mt-5">
                                    <TextBox id="ent-retention" name="recordingRetentionMonths" label="Recordings kept for (months)" labelUp block noMarginBottom numeric
                                        value={form.recordingRetentionMonths} onTextChanges={v => set("recordingRetentionMonths", v)} />
                                    <TextBox id="ent-support" name="supportTier" label="Support" labelUp block noMarginBottom
                                        value={form.supportTier} onTextChanges={v => set("supportTier", v)} />
                                </div>
                            </div>

                            {/* ------------------------------------------------ terms */}
                            <div className="ent-section">
                                <div className="ent-section-title">Terms</div>
                                <div className="ent-grid is-3">
                                    <TextBox id="ent-term" name="termMonths" label="Term (months)" labelUp block noMarginBottom numeric
                                        value={form.termMonths} onTextChanges={v => set("termMonths", v)} />
                                    <DropDown id="ent-billing" name="billing" label="Billed" labelUp block marginBottom={0} deselect={false} classicSelect
                                        source={[{ id: "monthly", name: "Monthly" }, { id: "annual", name: "Yearly" }]}
                                        defaultValue={form.billing} onSelect={v => v && set("billing", v as "monthly" | "annual")} />
                                    <DatePicker id="ent-valid-until" name="validUntil" label="Offer open until" labelUp block marginBottom={0}
                                        minDate={new Date()} preventClear
                                        selectedDate={moment(form.validUntil, "YYYY-MM-DD").format("DD/MM/YYYY")}
                                        onPick={e => set("validUntil", e.date.format("YYYY-MM-DD"))} />
                                    <TextBox id="ent-uplift" name="renewalUpliftPercent" label="Uplift at renewal" labelUp block noMarginBottom percent
                                        value={form.renewalUpliftPercent} onTextChanges={v => set("renewalUpliftPercent", v)} />
                                    <TextBox id="ent-payment" name="payment" label="Payment" labelUp block noMarginBottom readOnly
                                        value="Card, charged in advance" />
                                    <div className="d-flex align-items-end pb-2">
                                        <ToggleSwitch raw label="Renews automatically" checked={form.autoRenew} onChange={v => set("autoRenew", v)} />
                                    </div>
                                </div>
                                <div className="ent-grid mt-5">
                                    <TextArea id="ent-customer-note" name="customerNote" label="Note to the customer — shown on the offer" labelUp block noMarginBottom rows={3}
                                        value={form.customerNote} onChange={e => set("customerNote", e.target.value)} />
                                    <TextArea id="ent-internal-note" name="internalNote" label="Internal note — never shown to the customer" labelUp block noMarginBottom rows={3}
                                        value={form.internalNote} onChange={e => set("internalNote", e.target.value)} />
                                </div>
                            </div>
                        </div>

                        {/* ------------------------------------------------ the internal panel */}
                        <div className="ent-builder-side">
                            <div className="ent-panel">
                                <div className="ent-panel-title">
                                    Figures
                                    {pricing && <Icon icon="spinner-third" spin size={11} className="ms-2 text-gray-500" />}
                                </div>
                                <Row label="Monthly" value={toMoney(calc?.monthly)} strong />
                                {calc && calc.listMonthly !== calc.monthly && <Row label="At automatic prices" value={toMoney(calc.listMonthly)} muted />}
                                <Row label="One-time" value={toMoney(calc?.oneTime)} />
                                <Row label={form.billing === "annual" ? "Each year" : "Each month"} value={toMoney(calc?.periodPrice)} />
                                <Row label="Charged on accepting" value={toMoney(calc?.firstCharge)} />
                                <div className="ent-panel-divider" />
                                <Row label="Users / messages / minutes" value={`${toCount(calc?.includedUsers)} / ${toCount(calc?.includedMessages)} / ${toCount(calc?.includedMinutes)}`} />
                                <Row label="Per message" value={toRate(calc?.effectivePerMessage)} />
                                <Row label="Per user" value={toMoney(calc?.effectivePerUser)} />
                            </div>

                            <div className="ent-panel is-internal">
                                <div className="ent-panel-title"><Icon icon="lock" size={10} className="me-2" />Internal — never on the customer's copy</div>
                                <Row label="Discount" value={`${calc?.discountPercent ?? 0}%`} />
                                <Row label="Estimated cost / month" value={toMoney(calc?.estimatedMonthlyCost)} />
                                <Row label="Gross margin" value={`${calc?.marginPercent ?? 0}%`}
                                    tone={(calc?.marginPercent ?? 0) < (settings.directorBelowMargin) ? "bad" : "good"} />
                                <div className={"ent-approval is-" + (calc?.requiredApproval ?? "none")}>
                                    <Icon icon={calc?.requiredApproval === "none" ? "circle-check" : "user-shield"} type="solid" size={12} />
                                    {
                                        calc?.requiredApproval === "director" ? "Needs a director's approval"
                                            : calc?.requiredApproval === "manager" ? "Needs a sales manager's approval"
                                                : "No approval needed"
                                    }
                                </div>
                                <div className="text-gray-500 fs-9 mt-2">
                                    Up to {settings.selfApproveMaxDiscount}% off: no approval · up to {settings.managerApproveMaxDiscount}%: sales manager ·
                                    more, or margin under {settings.directorBelowMargin}%: director.
                                </div>
                            </div>

                            {
                                (calc?.blockers?.length ?? 0) > 0 &&
                                <div className="ent-panel is-blocked">
                                    <div className="ent-panel-title"><Icon icon="ban" size={10} className="me-2" />Cannot be sent yet</div>
                                    <ul>{calc!.blockers.map((b, i) => <li key={i}>{b}</li>)}</ul>
                                    <div className="text-gray-600 fs-9">A draft can be saved as it is.</div>
                                </div>
                            }
                        </div>
                    </div>
            }
        </DrawerForm>
    )
}


function Row(props: { label: string, value: string, strong?: boolean, muted?: boolean, tone?: "good" | "bad" }) {
    return (
        <div className={"ent-panel-row" + (props.strong ? " is-strong" : "") + (props.muted ? " is-muted" : "") + (props.tone ? " is-" + props.tone : "")}>
            <span>{props.label}</span><b>{props.value}</b>
        </div>
    )
}
